import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, MessageSquare, FolderKanban, TrendingUp, ArrowUpRight } from "lucide-react";
import { adminGetPosts, getProjects, getSubmissions } from "@/lib/api";

export default function AdminDashboard() {
  const [counts, setCounts] = useState({ posts: 0, projects: 0, submissions: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [posts, projects, submissions] = await Promise.all([
          adminGetPosts(),
          getProjects(),
          getSubmissions(),
        ]);
        setCounts({
          posts: posts?.length || 0,
          projects: projects?.length || 0,
          submissions: submissions?.length || 0,
        });
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Dashboard ]</p>
      <h1 className="mt-3 font-display text-4xl md:text-5xl tracking-tight">Studio overview.</h1>
      <p className="mt-3 text-zinc-400">Quick snapshot of content and inbound interest.</p>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Blog posts" value={counts.posts} link="/admin/blog" />
        <StatCard icon={FolderKanban} label="Projects" value={counts.projects} link="/projects" external />
        <StatCard icon={MessageSquare} label="Submissions" value={counts.submissions} link="/admin/submissions" />
        <StatCard icon={TrendingUp} label="Live site" value="↗" link="/" external />
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl glass border-white/[0.08] p-7">
          <h2 className="font-display text-2xl tracking-tight">Quick actions</h2>
          <ul className="mt-5 space-y-2 text-sm">
            <li>
              <Link to="/admin/blog?new=1" className="text-nexus-cyan hover:underline inline-flex items-center gap-1.5">
                + New blog post
              </Link>
            </li>
            <li>
              <Link to="/admin/submissions" className="text-nexus-cyan hover:underline">
                Review contact submissions
              </Link>
            </li>
          </ul>
        </div>
        <div className="rounded-2xl glass border-white/[0.08] p-7">
          <h2 className="font-display text-2xl tracking-tight">Resources</h2>
          <ul className="mt-5 space-y-2 text-sm text-zinc-400">
            <li>API docs at <code className="text-nexus-cyan">/api</code></li>
            <li>Token TTL: 8 hours, stored in localStorage</li>
            <li>Admin password rotation: update <code>.env</code> + restart backend</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, link, external }) {
  const inner = (
    <div className="rounded-2xl glass border-white/[0.08] p-6 hover:bg-white/[0.04] transition h-full">
      <div className="flex items-center justify-between mb-6">
        <Icon className="h-4 w-4 text-nexus-cyan" />
        <ArrowUpRight className="h-4 w-4 text-zinc-500" />
      </div>
      <div className="font-display text-4xl tracking-tighter">{value}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
  if (external) return <a href={link} target="_blank" rel="noreferrer">{inner}</a>;
  return <Link to={link} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link>;
}
